import * as React from 'react';

import { fileImageSrcSet } from '../../lib/file-image';
import { cn } from '../../lib/utils';
import { ImageLightbox, toGalleryImage, type GalleryImage } from './image-lightbox';

/**
 * The column and row counts a grid can take, as whole classes rather than a
 * number spliced into one.
 *
 * Tailwind reads the source as text and never runs it, so a class assembled at
 * runtime — `` `grid-cols-${columns}` `` — is a class it never sees and never
 * generates. It works in dev, where the JIT has the whole stylesheet, and the
 * grid silently collapses to one column in the production build. Writing each
 * one out is what keeps them in the output.
 *
 * Every column step is responsive rather than fixed: four across on a phone is
 * four thumbnails too small to make out, so each count names what it does at
 * the narrow end too. Row counts are not — a row count is a deliberate shape,
 * and quietly changing it by viewport would defeat asking for one.
 */
const GRID_COLUMNS = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
  6: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6',
} as const;

const GRID_ROWS = {
  1: 'grid-rows-1',
  2: 'grid-rows-2',
  3: 'grid-rows-3',
  4: 'grid-rows-4',
} as const;

export type ImageGalleryProps = {
  /** Plain URLs, or `{ src, alt }` when the pictures carry meaning. */
  images: ReadonlyArray<string | GalleryImage>;
  /**
   * Names the set for anyone who cannot see it — "MDS café hall", not
   * "gallery". It names the grid and it names the viewer that opens out of it,
   * so a screen-reader user who opens a picture is told what they are in.
   */
  label: string;
  /**
   * How the pictures are arranged.
   *
   * `grid` — every picture the same size. Shape it with `columns` and `rows`.
   *
   * `feature-right` — one picture large, the rest as a column of thumbnails
   * down its right-hand side. Stacks on a phone, where a rail beside a picture
   * leaves neither of them a usable width.
   *
   * `feature-below` — one picture large, the rest as a row of thumbnails under
   * it, from the left.
   *
   * Both `feature` layouts are two clicks rather than one: a thumbnail swaps
   * itself into the large frame, and the large frame opens the full-screen
   * viewer. A thumbnail that jumped straight to full screen would make the
   * large frame decorative.
   *
   * `marquee` — one row of large pictures drifting left on a loop. It glides
   * to a stop while the pointer is over it or
   * focus is inside it, because a picture that slides out from under the
   * cursor cannot be clicked, and glides back up after. With reduced motion on
   * it stands still and scrolls sideways by hand. Ignores `columns` and `rows`.
   */
  layout?: 'grid' | 'feature-right' | 'feature-below' | 'marquee';
  /**
   * `grid` only — how many pictures sit across the widest breakpoint.
   *
   * With `rows`, the grid is exactly that shape. Alone, the pictures run to as
   * many rows as they need. Omitted with `rows` set, they flow sideways into as
   * many columns as they need, and the row scrolls.
   */
  columns?: keyof typeof GRID_COLUMNS;
  /** `grid` only — how many rows. See `columns` for how the two combine. */
  rows?: keyof typeof GRID_ROWS;
  className?: string;
};

/**
 * A set of photographs in one of three arrangements, any of which opens
 * full-screen.
 *
 * The pair to `ImageLightbox`, and the reason that one is controlled: the
 * viewer holds no state, this holds the indices, and a thumbnail, the arrow
 * keys and a swipe all move the same numbers. There is no second source of
 * truth that can disagree about which picture is showing.
 *
 * Use the viewer directly instead when the thing that opens it is not a set of
 * thumbnails — a card's photograph, a row of attachments, a link in a
 * sentence. This component is the arrangement, not the mechanism.
 */
export function ImageGallery({
  images,
  label,
  layout = 'grid',
  columns,
  rows,
  className,
}: ImageGalleryProps) {
  const pictures = React.useMemo(() => images.map(toGalleryImage), [images]);
  const count = pictures.length;

  // Which picture is in the large frame, and which one the viewer is showing.
  // Two separate things: closing the viewer should leave the large frame on
  // whatever you were last looking at, not snap it back.
  const [featured, setFeatured] = React.useState(0);
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);

  // `marquee` only. Hover and focus glide the strip to a stop and leaving
  // glides it back up, rather than `animation-play-state: paused`, which
  // halts it dead mid-slide and restarts it just as abruptly. The CSS
  // animation is left running and only its playback rate is steered, over
  // `--duration-base`, easing out. With reduced motion there is no animation
  // to steer and this does nothing.
  const trackRef = React.useRef<HTMLDivElement>(null);
  const glideRef = React.useRef<(target: number) => void>(() => {});

  React.useEffect(() => {
    const track = trackRef.current;
    if (layout !== 'marquee' || !track) return;
    const animation = track.getAnimations()[0];
    if (!animation) return;

    const duration = parseFloat(getComputedStyle(track).getPropertyValue('--duration-base')) || 0;
    let frame = 0;

    glideRef.current = (target) => {
      cancelAnimationFrame(frame);
      const from = animation.playbackRate;
      const start = performance.now();
      const step = (now: number) => {
        const t = duration ? Math.min(1, (now - start) / duration) : 1;
        animation.playbackRate = from + (target - from) * (1 - (1 - t) ** 3);
        if (t < 1) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    };

    return () => {
      cancelAnimationFrame(frame);
      glideRef.current = () => {};
    };
  }, [layout, count]);

  // A shorter list than the one we are sitting on would leave `featured` past
  // the end and render an empty frame with nothing to explain it.
  const lead = count > 0 ? Math.min(featured, count - 1) : 0;

  if (count === 0) return null;

  const viewer = (
    <ImageLightbox
      images={pictures}
      label={label}
      activeIndex={activeIndex}
      onClose={() => setActiveIndex(null)}
      onIndexChange={(next) => {
        setActiveIndex(next);
        // Moving inside the viewer moves the large frame with it, so closing
        // leaves the page on the picture that was on screen a moment ago.
        setFeatured(next);
      }}
    />
  );

  if (layout === 'grid') {
    /* Three shapes out of two optional props:
     *   columns + rows  an exact grid. More pictures than cells is allowed and
     *                   they flow into implicit rows — dropping them silently
     *                   would be the worse of the two surprises.
     *   rows only       as many columns as it takes, flowing sideways, and the
     *                   band scrolls. `auto-cols` is what gives those implicit
     *                   columns a width; without it they collapse to content.
     *   columns only    the ordinary case, and the default when neither is set.
     */
    const shape =
      columns && rows
        ? cn(GRID_COLUMNS[columns], GRID_ROWS[rows])
        : rows
          ? cn(GRID_ROWS[rows], 'grid-flow-col auto-cols-[minmax(11rem,1fr)] overflow-x-auto')
          : GRID_COLUMNS[columns ?? 4];

    return (
      <>
        <ul className={cn('grid list-none gap-4', shape, className)}>
          {pictures.map((image, i) => (
            <li key={`${image.src}-${i}`}>
              <GalleryTile
                image={image}
                verb="Open"
                position={i + 1}
                count={count}
                onClick={() => setActiveIndex(i)}
                className="aspect-card"
              />
            </li>
          ))}
        </ul>
        {viewer}
      </>
    );
  }

  if (layout === 'marquee') {
    /* The pictures twice, end to end, and the `marquee` keyframe moves the
     * track by half its width, so the loop has no seam. `pr-4` matches the gap,
     * so the space after one copy's last picture equals the space between any
     * two — without it the seam shows.
     *
     * The second copy is a visual repeat only: hidden from screen readers and
     * out of the tab order, so nobody hears or tabs through the set twice. */
    const strip = (repeat: boolean) => (
      <ul
        aria-hidden={repeat || undefined}
        className={cn('flex shrink-0 list-none gap-4 pr-4', repeat && 'motion-reduce:hidden')}
      >
        {pictures.map((image, i) => (
          <li key={`${image.src}-${i}`} className="shrink-0">
            <GalleryTile
              image={image}
              verb="Open"
              position={i + 1}
              count={count}
              onClick={() => setActiveIndex(i)}
              tabIndex={repeat ? -1 : undefined}
              className="aspect-card w-72 sm:w-96"
            />
          </li>
        ))}
      </ul>
    );

    return (
      <>
        {/* `py-1` because the focus ring is drawn outside the tile, and the
            clip would otherwise cut it off top and bottom.

            A faint fade at the ends: the edge only dims to 70%, never to
            nothing. Fading all the way out read as too much even when it was
            narrow; what softens the edge is the dimming, not the vanishing. */}
        <div
          role="group"
          aria-label={label}
          onPointerEnter={() => glideRef.current(0)}
          onPointerLeave={() => glideRef.current(1)}
          onFocus={() => glideRef.current(0)}
          onBlur={(e) => {
            // Only when focus leaves the strip, not when it moves between tiles.
            if (!e.currentTarget.contains(e.relatedTarget)) glideRef.current(1);
          }}
          className={cn(
            'overflow-hidden py-1 motion-reduce:overflow-x-auto',
            '[mask-image:linear-gradient(to_right,color-mix(in_srgb,black_70%,transparent),black_6%,black_94%,color-mix(in_srgb,black_70%,transparent))]',
            className,
          )}
        >
          <div ref={trackRef} className="flex w-max animate-marquee motion-reduce:animate-none">
            {strip(false)}
            {strip(true)}
          </div>
        </div>
        {viewer}
      </>
    );
  }

  // The two feature layouts are one structure. On a phone they are the same:
  // the picture, then one strip under it. `feature-right` differs only from
  // `sm` up, where the strip becomes a rail beside the picture.
  const thumbnails = (
    <ul
      className={cn(
        // The phone strip: one row, scrolling sideways. Wrapping fitted three
        // across and stacked the rest into rows that outgrew the picture.
        // `py-1` so the clip does not cut the focus ring.
        'flex list-none gap-3 max-sm:overflow-x-auto max-sm:py-1',
        layout === 'feature-below'
          ? // From the left, wrapping onto further rows rather than scrolling:
            // a strip under a picture has the full width to use, so there is no
            // reason to hide half of it behind a scroll.
            'sm:flex-wrap'
          : // The rail. Absolute from `sm` up so its height is the large
            // picture's and not the other way round — as a normal flex child a
            // tenth thumbnail would make the rail taller than the photograph
            // beside it and stretch the whole block to match.
            'sm:absolute sm:inset-0 sm:flex-col sm:overflow-y-auto',
      )}
    >
      {pictures.map((image, i) => (
        <li
          key={`${image.src}-${i}`}
          // Full width on the phone strip, which scrolls rather than squeezes.
          //
          // On the rail each thumbnail is 4:3 from the top down, and never
          // grows past that: stretching them to share a tall rail turned them
          // into portrait slivers. They may shrink instead — more of them than
          // the rail holds get shorter (wider in shape, never taller) rather
          // than overflowing, and only below the floor does the rail scroll.
          className={cn('shrink-0', layout === 'feature-right' && 'sm:min-h-16 sm:shrink')}
        >
          <GalleryTile
            image={image}
            verb="Show"
            position={i + 1}
            count={count}
            selected={i === lead}
            onClick={() => setFeatured(i)}
            className={cn(
              'aspect-card w-24',
              // `size-full` with the ratio kept: at its natural height the item
              // is the 4:3 box; shrunk, the height it is given wins and the
              // picture crops to the wider shape.
              layout === 'feature-right' && 'sm:size-full',
            )}
          />
        </li>
      ))}
    </ul>
  );

  const feature = (
    <GalleryTile
      image={pictures[lead]!}
      verb="Open"
      position={lead + 1}
      count={count}
      onClick={() => setActiveIndex(lead)}
      // Capped by width, not height, so it stays 4:3 and shrinks whole.
      //
      // `h-full` is inert until something gives the gallery a height: against
      // an auto-height parent a percentage height is auto and the ratio rules.
      // Stretched by a row it sits in — beside a card it has to match — the
      // frame fills that height instead and `object-cover` crops to fit.
      className="aspect-card h-full w-full max-w-gallery-feature"
    />
  );

  // One picture has nothing to switch to: a strip holding the same photo the
  // frame already shows would be a control that does nothing. The large frame
  // alone is the gallery then, still opening the viewer.
  const hasThumbnails = count > 1;

  return (
    <>
      <div className={cn('flex flex-col gap-4', layout === 'feature-right' && 'sm:flex-row', className)}>
        {/* Takes what the strip leaves. Stretched by a row beside a card, the
            frame's `h-full` would otherwise claim the whole height and push
            the thumbnails out underneath; at auto height this is inert and
            the frame keeps its 4:3. Capped with the frame, so a rail sits
            against the picture rather than across a gap at the far edge. */}
        <div className="max-w-gallery-feature min-h-0 min-w-0 flex-1">{feature}</div>
        {/* The rail's positioning context. It stretches to the row height,
            which the large picture alone decides, and the absolute list inside
            it fills that. */}
        {hasThumbnails && (
          <div className={cn('relative shrink-0', layout === 'feature-right' && 'sm:w-28')}>
            {thumbnails}
          </div>
        )}
      </div>
      {viewer}
    </>
  );
}

/**
 * One clickable photograph — a grid cell, a thumbnail or the large frame. They
 * differ in size and in what the click does, and in nothing else, which is why
 * the size arrives as a class and the verb as a word.
 */
function GalleryTile({
  image,
  verb,
  position,
  count,
  selected,
  onClick,
  tabIndex,
  className,
}: {
  image: GalleryImage;
  /** What the click does, for the label: opens the viewer, or shows it large. */
  verb: 'Open' | 'Show';
  position: number;
  count: number;
  selected?: boolean;
  onClick: () => void;
  /** `-1` for a marquee's repeated copy, which must not be tabbed through twice. */
  tabIndex?: number;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      tabIndex={tabIndex}
      aria-current={selected || undefined}
      /* Names the action, not the picture — which is why a position is allowed
         here while an invented `alt` is not. "Open image 3 of 6" says what the
         button does and claims nothing about what is in it; an `alt` of
         "Image 3" would be read as description, and would be a lie. A real
         description beats the position, so it wins when there is one. */
      aria-label={image.alt ? `${verb} ${image.alt}` : `${verb} image ${position} of ${count}`}
      className={cn(
        'group block overflow-hidden rounded-card bg-placeholder',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        // The unselected thumbnails sit back so the chosen one reads as chosen.
        // Only ever passed on a rail or a strip — in a grid nothing is
        // selected, and dimming every tile but one would be a lie about state.
        //
        // That dimming is the whole mark: the selected tile draws no ring of
        // its own. It is the one picture at full strength beside a rail of
        // held-back ones, and an edge around it as well would be the same
        // thing said twice — the second time in a colour that competes with
        // the photograph it is meant to be framing. `aria-current` is what
        // carries the state to anyone who cannot see the difference, and the
        // focus ring is still its own, separate mark.
        selected === false && 'opacity-60 hover:opacity-100',
        className,
      )}
    >
      {/* `alt=""` deliberately: the button around it already carries the
          description, and a screen reader that reads both says the same
          sentence twice. */}
      <img
        src={image.src}
        srcSet={fileImageSrcSet(image.src)}
        // A tile is a fraction of the row: a third or so on a wide screen, half on a phone.
        sizes="(min-width: 1024px) 33vw, 50vw"
        alt=""
        loading="lazy"
        decoding="async"
        draggable={false}
        className={cn(
          'size-full object-cover',
          'transition-transform duration-(--duration-base) ease-standard',
          'group-hover:scale-105 motion-reduce:transition-none',
        )}
      />
    </button>
  );
}
